import React from 'react'
import { createIntl, createIntlCache } from 'react-intl'

import axios, { AxiosInstance } from 'axios'
import { uniq } from 'lodash-es'
import SumoLogger from 'sumo-logger'

import { sanitizeData } from './userUtils'
import { getFormattedMessageSafe } from '../../intl/utils/getFormattedMessageSafe'
import {
  CARE_ADVISOR_EMAIL,
  DOMESTIC_PROVIDER_CONTACT_EMAIL,
  GOODNESS_PROD_SPANISH_URL,
  GOODNESS_PROD_URL,
  GOODNESS_STAGING_SPANISH_URL,
  GOODNESS_STAGING_URL,
  ICAS_CONTACT_EMAIL,
  INTERNATIONAL_PROVIDER_CONTACT_EMAIL,
  IS_PRODUCTION,
  LYRA_CLINICAL_CUSTOMER_NAMES,
  LYRA_CLINICAL_SUPPORT_EMAIL,
  LYRA_HEALTH_SUPPORT_EMAIL,
  LYRIANS_CUSTOMER_NAMES,
  PERSIST_STORE_KEY,
} from '../constants/constants'
import {
  allOptionsEligibleTreatmentOptions,
  TREATMENT_OPTIONS_FROM_FRONT_END,
  TREATMENT_OPTIONS_TYPE,
} from '../constants/treatmentOptions'

// checks if the document object exists and returns it, else undefined
export const getDocumentObject = () => {
  return typeof document !== 'undefined' ? document : undefined
}

export type SumoLogLevel = 'INFO' | 'WARN' | 'ERROR'

/**
 * Common function to handle logging to sumologic
 * expects user to pass instance of sumo logic
 */
export const logToSumoLogic = (
  sumoLogger: SumoLogger,
  options: SumoLogger.SumoLoggerOptions,
  sourceCategory: string,
  sourceName: string,
  userId: string,
  data: Dict,
) => {
  let logData = {}
  if (data instanceof FormData) {
    for (const entry in data) {
      logData[entry[0]] = entry[1]
    }
  } else {
    logData = data
  }
  logData = sanitizeData(logData)
  // Ignoring the updateConfig since the @types for sumo-logic don't include it :(
  ;(sumoLogger as $TSFixMe).updateConfig({ sourceCategory, sourceName })
  sumoLogger.log({ sessionKey: userId, ...options, ...logData })
}

// Finds and sorts matches in array. Used by typeAheads
export const matchFinder = (options: string[], text: string, locale: string) => {
  const collator = new Intl.Collator()
  const searchQueryFormatted = text.toLocaleLowerCase(locale)
  const optionsFormatted = options.map((option) => option.toLocaleLowerCase(locale))
  const startsWith = options.filter((option, index) => optionsFormatted[index].startsWith(searchQueryFormatted))
  const includes = options.filter((option, index) => optionsFormatted[index].includes(searchQueryFormatted))
  const results = uniq([...startsWith, ...includes])
  return results.sort((a, b) => collator.compare(a, b))
}

// Finds and sorts matches across multiple searchable fields on objects, returning deduplicated display strings. Used by typeAheads that search objects with multiple name fields.
export const matchFinderForObjects = <T>(
  items: T[],
  getSearchableStrings: (item: T) => (string | undefined)[],
  getDisplayString: (item: T) => string,
  text: string,
  locale: string,
): string[] => {
  const collator = new Intl.Collator()
  const lowerText = text.toLocaleLowerCase(locale)
  const matched = items.filter((item) =>
    getSearchableStrings(item).some((s) => s?.toLocaleLowerCase(locale).includes(lowerText)),
  )
  return uniq(matched.map(getDisplayString)).sort((a, b) => collator.compare(a, b))
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export const convertObjToFormData = (input: {}) => {
  return Object.keys(input).reduce((formData, key) => {
    formData.append(key, input[key])
    return formData
  }, new FormData())
}

export const getHelpEmailAddress = ({
  customerName,
  isInternationalUser,
  isLyraForLyrians2026Enabled,
}: {
  customerName: string
  isInternationalUser: boolean
  isLyraForLyrians2026Enabled: boolean
}) => {
  if (Object.values(LYRIANS_CUSTOMER_NAMES).includes(customerName) && isLyraForLyrians2026Enabled) {
    if (Object.values(LYRA_CLINICAL_CUSTOMER_NAMES).includes(customerName)) {
      return LYRA_CLINICAL_SUPPORT_EMAIL
    } else {
      return LYRA_HEALTH_SUPPORT_EMAIL
    }
  }
  return isInternationalUser ? ICAS_CONTACT_EMAIL : CARE_ADVISOR_EMAIL
}

export const getProviderContactEmailAddress = ({ isInternationalUser }: { isInternationalUser: boolean }) => {
  return isInternationalUser ? INTERNATIONAL_PROVIDER_CONTACT_EMAIL : DOMESTIC_PROVIDER_CONTACT_EMAIL
}

export const isWorkHubSubPath = (subPath: string) => {
  return subPath.length > 0 && subPath.startsWith('/work-hub')
}

/**
 * Can be used when you need a timeout that you need to await for - ex. Waiting 3 seconds before auto-navigating
 */
export const promiseTimeout = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export const shuffleArray = <T>(array: T[]): T[] => {
  const arrayCopy = [...array]
  for (let i = arrayCopy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp = arrayCopy[i]
    arrayCopy[i] = arrayCopy[j]
    arrayCopy[j] = temp
  }
  return arrayCopy
}

/**
 * Deterministically shuffles an array based on a UUID seed.
 *
 * This function provides a stable, reproducible shuffle - the same UUID will always
 * produce the same shuffled order for a given array. This is useful when you need
 * consistent randomization across sessions or users, such as:
 * - Personalizing content order per user while keeping it consistent
 * - A/B testing with stable variant assignments
 * - Displaying provider keywords in a randomized but repeatable order
 *
 * @template T - The type of elements in the array
 * @param array - The array to shuffle
 * @param uuid - A UUID string used as the seed for deterministic shuffling.
 *               Dashes are automatically removed, so both formats work:
 *               - "21c6ca09-52a8-4f8b-8926-91f095181773"
 *               - "21c6ca0952a84f8b892691f095181773"
 * @returns A new shuffled array (does not mutate the original)
 *
 * @example
 * // Shuffle provider keywords consistently per provider
 * const keywords = ['empathetic', 'experienced', 'supportive', 'collaborative']
 * const providerId = '21c6ca09-52a8-4f8b-8926-91f095181773'
 * const shuffled = stableShuffleArrayWithUUID(keywords, providerId)
 * // Result: ['supportive', 'empathetic', 'collaborative', 'experienced']
 * // Same providerId always produces the same order
 *
 * @example
 * // Different UUIDs produce different shuffles
 * const items = ['A', 'B', 'C', 'D']
 * const user1 = stableShuffleArrayWithUUID(items, 'uuid-1')
 * const user2 = stableShuffleArrayWithUUID(items, 'uuid-2')
 * // user1 and user2 will have different orders, but each is stable
 *
 * @example
 * // Original array is not mutated
 * const original = [1, 2, 3, 4, 5]
 * const shuffled = stableShuffleArrayWithUUID(original, 'some-uuid')
 * console.log(original) // [1, 2, 3, 4, 5] - unchanged
 *
 * @see {@link shuffleArray} for non-deterministic random shuffling
 * @see {@link getProviderKeywords} in providers/utils.ts for real-world usage
 */
export const stableShuffleArrayWithUUID = <T>(array: T[], uuid: string): T[] => {
  // Remove dashes and take first 8 characters, then convert hex to number
  const cleanUuid = uuid.replace(/-/g, '')
  const hexSeed = cleanUuid.substring(0, 8)
  const numericSeed = parseInt(hexSeed, 16)

  // Create a copy to avoid mutating original array
  const shuffledArray = [...array]

  // Shuffle with deterministic seed
  for (let currentIndex = shuffledArray.length - 1; currentIndex > 0; currentIndex--) {
    // Generate deterministic "random" index based on seed and current position
    const swapIndex = (numericSeed + currentIndex) % (currentIndex + 1)

    // Swap elements
    const temp = shuffledArray[currentIndex]
    shuffledArray[currentIndex] = shuffledArray[swapIndex]
    shuffledArray[swapIndex] = temp
  }

  return shuffledArray
}

export const getRandomItems = <T>(array: T[], n: number): T[] => {
  const arrayCopy = shuffleArray(array)
  return arrayCopy.slice(0, n)
}

export const getGoodnessBaseUrl = (displayLanguage?: string) => {
  const isSpanish = displayLanguage?.startsWith('es')
  let urls = {
    prod: GOODNESS_PROD_URL,
    staging: GOODNESS_STAGING_URL,
  }
  if (isSpanish) {
    urls = {
      prod: GOODNESS_PROD_SPANISH_URL,
      staging: GOODNESS_STAGING_SPANISH_URL,
    }
  }
  return IS_PRODUCTION ? urls.prod : urls.staging
}

export const mapObjectValues = <T, U>(object: { [key: string]: T }, callback: (value: T, key: string) => U) => {
  return Object.keys(object).reduce((acc, key) => {
    acc[key] = callback(object[key], key)
    return acc
  }, {} as { [key: string]: U })
}

// Get the current year primarily for program coverage breakdowns by year
export const getCurrentYear = () => new Date().getFullYear().toString()

/**
 * Helper function to assert that a code path is unreachable.
 *
 * This can be used as the default case in a switch statement to check for exhaustiveness.
 * It will raise a type error if the _neverArg type includes any cases that are not handled.
 */
export function assertUnreachable(_neverArg: never, message?: string) {
  if (__DEV__) {
    throw new Error(message ?? "Didn't expect to get here")
  }
}

export const isValidTreatmentOptionForDirectPath = (value?: string): boolean => {
  if (value == undefined) {
    return false
  }

  return allOptionsEligibleTreatmentOptions
    .concat([TREATMENT_OPTIONS_FROM_FRONT_END.CLINICAL_LEAVE_EVALUATION])
    .includes(value as TREATMENT_OPTIONS_TYPE)
}

let essentialsCmsAxiosInstance: AxiosInstance | null = null

export const getEssentialsCmsAxiosInstance = (essentialsCmsUrl: string): AxiosInstance => {
  if (!essentialsCmsAxiosInstance) {
    essentialsCmsAxiosInstance = axios.create({
      baseURL: essentialsCmsUrl,
    })
  }
  return essentialsCmsAxiosInstance
}

export const transformBooleanStringsInObject = (obj: Dict) => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, value === 'true' ? true : value === 'false' ? false : value]),
  )
}

/**
 * Clear out 1Lyra persistent store keys on logout from LW
 */
export const clearPersistStoreKeys = (keys: string[]) => {
  const stored = localStorage.getItem(PERSIST_STORE_KEY)
  if (!stored) return

  try {
    const parsed = JSON.parse(stored)
    if (parsed.persistSecure) {
      const persistSecure = JSON.parse(parsed.persistSecure)
      keys.forEach((key) => delete persistSecure[key])
      parsed.persistSecure = JSON.stringify(persistSecure)
      localStorage.setItem(PERSIST_STORE_KEY, JSON.stringify(parsed))
    }
  } catch (e) {
    console.warn('Failed to clear persist store keys:', e)
  }
}

export const generateAwsTraceId = (contextUuid: string, sessionUuid?: string | null) => {
  const epoch = Math.floor(Date.now() / 1000).toString(16)
  const random_uuid = sessionUuid ? sessionUuid.slice(0, 12) + contextUuid.slice(12) : contextUuid
  const random = random_uuid.replace(/-/g, '').slice(0, 24)
  let awsTraceId = `Root=1-${epoch}-${random};lyra-request-id=${contextUuid}`
  if (sessionUuid) {
    awsTraceId += `;lyra-session-id=${sessionUuid}`
  }
  return awsTraceId
}

export const getStringFromReactElement = (element: React.ReactNode | undefined) => {
  if (element && React.isValidElement(element)) {
    const { id, defaultMessage, description, values } = element.props
    const englishIntl = createIntl({ locale: 'en', messages: {} }, createIntlCache())
    return getFormattedMessageSafe({ id, defaultMessage, description }, englishIntl.formatMessage, values)
  }
  return undefined
}
